import { Box, Flex, Image, Text } from "@chakra-ui/react";
import type { Companion } from "src/models/companion";
import { withAlpha } from "src/theme";
import { ThemeBadge } from "./ThemeBadge";

/** A tile with the scene picture: grid size or a large featured card. */
export function CompanionCard({
  companion,
  onOpen,
  featured = false,
  index = 0,
}: {
  companion: Companion;
  onOpen: (c: Companion) => void;
  featured?: boolean;
  /** Position in its list, to stagger the entrance animation. */
  index?: number;
}) {
  return (
    <Box
      as="button"
      onClick={() => onOpen(companion)}
      position="relative"
      textAlign="left"
      width="100%"
      aspectRatio={featured ? "4 / 5" : "3 / 4"}
      borderRadius={featured ? "28px" : "22px"}
      overflow="hidden"
      bg={withAlpha(companion.color, 0.3)}
      boxShadow={`0 10px 30px ${withAlpha(companion.color, featured ? 0.28 : 0.16)}`}
      transition="transform 0.18s ease"
      _active={{ transform: "scale(0.97)" }}
      animation={`tlRise 0.45s ease both`}
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
      cursor="pointer"
    >
      {companion.avatarURL && (
        <Image
          src={companion.avatarURL}
          alt=""
          position="absolute"
          inset={0}
          boxSize="100%"
          objectFit="cover"
          draggable={false}
          loading="lazy"
        />
      )}
      <Box
        position="absolute"
        inset={0}
        bg={`linear-gradient(180deg, rgba(0,0,0,0) 35%, ${withAlpha("#0e0a0a", 0.55)} 62%, rgba(14,10,10,0.95) 100%)`}
      />
      <Box position="absolute" top={3} left={3}>
        <ThemeBadge theme={companion.theme} />
      </Box>
      <Flex
        position="absolute"
        left={0}
        right={0}
        bottom={0}
        direction="column"
        p={featured ? 5 : 3}
        gap={featured ? 1.5 : 0.5}
      >
        <Text
          fontSize={featured ? "28px" : "lg"}
          fontWeight="bold"
          lineHeight="1.1"
          color="white"
          letterSpacing="-0.01em"
        >
          {companion.name}
        </Text>
        <Text
          fontSize={featured ? "sm" : "xs"}
          color="rgba(255,255,255,0.78)"
          lineClamp={featured ? 2 : 2}
        >
          {companion.tagline}
        </Text>
        {featured && (
          <Flex
            mt={3}
            align="center"
            gap={2}
            alignSelf="flex-start"
            px={4}
            h="38px"
            borderRadius="full"
            bg="white"
            color="black"
            fontSize="sm"
            fontWeight="semibold"
          >
            Say hi
            <Box as="span" fontSize="md">
              →
            </Box>
          </Flex>
        )}
      </Flex>
    </Box>
  );
}
